import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CommonUtilsService, dayjs, RootState } from '@umanforge/common';

import { PlusIcon } from '@common_components/icons/plus.icon';

import BaseModal from '../../modal/components/base_modal.component';
//import SelectInput, { GroupedOption } from '@common_components/inputs/selects/selectInput.component';
import DatePickerSoloInput from '@common_components/inputs/datePickerInputSolo.component';
import BottomModal from '@common_components/modals/bottomModal.component';
import { RecurrenceType } from '../../task_modal/components/recurrenceModal.component';
// import Counter from 'components/inputs/counter';
import { ModifyTimeGridRecurrence, PostTimeGridRecurrence, TimeGridRecurrenceItem } from 'interfaces/timegridRecurrence.interface';
import { TimeGrid } from 'interfaces/timegrid.interface';
import WeekDaysSelect, { DaysToShow } from 'features/week_days_select/components/weekDaySelect.component';
import InputText from '@common_components/inputs/textInput.component';
import { TimeGridRecurrenceService } from 'services/timeGridRecurrence.service';
import { useDispatch, useSelector } from 'react-redux';
import ConfirmationModal from '@common_components/modals/confirmationModal.component';
import { ToastService } from 'services/toast.service';
import { ORDINALS } from 'constants/select.constants';
import { EditBoxIcon } from '@common_components/icons/editBox.icon';
import { TimeGridIcon } from 'components/icons/timegrid.icon';
import SelectInput, { GroupedOption } from '@common_components/inputs/selects/selectInput.component';
import { Tooltip } from 'react-tooltip';

interface TimeGridError {
  timeStart: string | undefined;
  timeEnd: string | undefined;
  recurrence: string | undefined;
}

interface TimeGridProps {
  sectorId: string;
  timeGridRecurrence: TimeGridRecurrenceItem;
  timegrids: TimeGrid[];
  onClose: () => void;
  usersId?: string[] | undefined;
  disable?: boolean;
  isWeekEnd: boolean;
  canHaveRecurrence: boolean;
}

const TimeGridModal: React.FC<TimeGridProps> = ( {
  sectorId,
  timeGridRecurrence,
  timegrids,
  onClose,
  disable = false,
  usersId,
  isWeekEnd,
  canHaveRecurrence
} ) => {

  const { t } = useTranslation();
  const token = useSelector( ( state: RootState ) => { return state.token.value; } );
  const dispatch = useDispatch();

  const valueToDays = ( val: number ) => {
    const weekMatrix = [ 0, 0, 0, 0, 0, 0, 0 ];
    for ( let i = 0; val; i++, val >>= 1 ) {
      if ( val & 1 ) { weekMatrix[ i ] = 1; };
    }
    return {
      monday: weekMatrix[ 1 ] != 0,
      tuesday: weekMatrix[ 2 ] != 0,
      wednesday: weekMatrix[ 3 ] != 0,
      thursday: weekMatrix[ 4 ] != 0,
      friday: weekMatrix[ 5 ] != 0,
      saturday: weekMatrix[ 6 ] != 0,
      sunday: weekMatrix[ 0 ] != 0
    };
  };

  const toastService = new ToastService( 'TimeGridRecurrenceModal' );

  const [ savedTimeGridRecurrence, ] = useState( timeGridRecurrence );

  //const [ priority, setPriority ] = useState( timeGridRecurrence.priority ?? 1 );
  const [ repeat, setRepeat ] = useState( timeGridRecurrence.repeat ?? 1 );
  const [ repeatType, setRepeatType ] = useState( timeGridRecurrence.repeatType ?? 0 );
  const [ type, setType ] = useState<RecurrenceType>( ( timeGridRecurrence.type ?? 'daily' ).toLowerCase() as RecurrenceType );

  const defaultTimeStart = timeGridRecurrence.timeStart ? dayjs( timeGridRecurrence.timeStart ) : dayjs( timeGridRecurrence.date );
  const defaultTimeEnd = timeGridRecurrence.timeEnd ? dayjs( timeGridRecurrence.timeEnd ) : undefined;
  const [ timeStart, setTimeStart ] = useState( defaultTimeStart );
  const [ timeEnd, setTimeEnd ] = useState( defaultTimeEnd );

  const [ selectedDays, setSelectedDays ] = useState<DaysToShow>( timeGridRecurrence.value ? valueToDays( timeGridRecurrence.value ) : { sunday: false, monday: true, tuesday: true, wednesday: true, thursday: true, friday: true, saturday: false } );
  const [ haveRecurrence, setHaveRecurrence ] = useState( timeGridRecurrence.id != null && timeGridRecurrence.priority != 10 );

  const [ selectedTimeGrid, setSelectedTimegrid ] = useState< string | undefined >( timeGridRecurrence.timeGrid?.id );

  const [ haveChanged, setHaveChanged ] = useState( false );
  const [ isValid, setIsValid ] = useState( false );
  const [ isFirstRender, setIsFirstRender ] = useState( false );

  const [ isDeleteTimeGridRecurrenceModalOpen, setIsDeleteTimeGridRecurrenceModalOpen ] = useState< boolean >( false );

  const [ errors, setErrors ] = useState<TimeGridError>( { timeStart: undefined, timeEnd: undefined, recurrence: undefined } );

  useEffect( () => {
    init();
  }, [] );

  useEffect( () => {
    if ( !isFirstRender ) {
      handleChanges();
    }
  }, [ selectedTimeGrid, timeStart, timeEnd, selectedDays, repeat, repeatType, type, haveRecurrence ] );

  const init = () => {
    handleChanges();
    setIsFirstRender( false );
  };


  const checkChanges = () => {
    if ( timeGridRecurrence.id == null ) {
      return selectedTimeGrid != null;
    }

    return (
      timeGridRecurrence.timeGrid?.id != selectedTimeGrid ||
      timeStart.format( 'YYYY-MM-DD' ) != savedTimeGridRecurrence.timeStart ||
      ( ( timeEnd ? timeEnd?.format( 'YYYY-MM-DD' ) : undefined ) != ( savedTimeGridRecurrence.timeEnd ?? undefined ) ) ||
      daysToValue( selectedDays ) != savedTimeGridRecurrence.value ||
      repeat != savedTimeGridRecurrence.repeat ||
      repeatType != savedTimeGridRecurrence.repeatType ||
      type != savedTimeGridRecurrence.type.toLowerCase()
    );
  };

  const checkValidity = () => {
    let valid = selectedTimeGrid != null && timeStart != null;
    const _errors: TimeGridError = { timeStart: undefined, timeEnd: undefined, recurrence: undefined };

    if ( timeGridRecurrence.id == null && timeStart.isBefore( dayjs().endOf( 'day' ) ) ) {
      valid = false;
      _errors.timeStart = t( 'translation:errors.start_date_is_before_today' );
    }

    if ( timeEnd != null && timeEnd.isBefore( timeStart ) ) {
      valid = false;
      _errors.timeEnd = t( 'translation:errors.end_date_is_before_date_start' );
    }

    if ( type == 'weekly' && daysToValue( selectedDays ) == 0 ) {
      valid = false;
      _errors.recurrence = t( 'translation:errors.it_needs_at_least_one_day_weekly' );
    }

    setErrors( _errors );
    return valid;
  };

  const handleChanges = () => {
    setHaveChanged( checkChanges() );
    setIsValid( checkValidity() );
  };


  // eslint-disable-next-line require-await
  const getRecurrenceType = async (): Promise<GroupedOption[]> => {
    const selectOptions = objectToGroupedOption(  [ 'daily', 'weekly', 'monthly' ] );
    return selectOptions;
  };


  // eslint-disable-next-line require-await
  const getTimeGridsOptions = async (): Promise<GroupedOption[]> => {
    return [ { options: CommonUtilsService.sortByKey(
      ( timegrids.filter( tg => { return !tg.disabled; } ) ?? [] ).map( tg => { return { label: tg.name, id: tg.id }; } ),
      'label',
      'asc'
    ) } ];
  };

  const objectToGroupedOption = ( values: string[] ): GroupedOption[] => {
    if ( values.length > 0 ) {
      return [ { options: CommonUtilsService.sortByKey(
        values.map( ( val, index ) => { return { label: getLitteral( val ), id: val, index: index }; } ),
        'index',
        'asc'
      ) } ];
    }
    return [];
  };

  const daysToValue = ( days: DaysToShow ) => {
    let res = 0;

    res += days.sunday ? 1 : 0;
    res += days.monday ? 2 : 0;
    res += days.tuesday ? 4 : 0;
    res += days.wednesday ? 8 : 0;
    res += days.thursday ? 16 : 0;
    res += days.friday ? 32 : 0;
    res += days.saturday ? 64 : 0;

    return res;
  };

  const getLitteral = ( recurrenceType: string ): string => {
    let litteral = recurrenceType;
    switch ( recurrenceType ) {
    case 'daily': litteral = 'day'; break;
    case 'weekly': litteral = 'weekOfYear'; break;
    case 'monthly': litteral = 'month'; break;
    case 'yearly': litteral = 'year'; break;
    default: ;
    }
    try {
      const res =  new Intl.DisplayNames( dayjs.locale(), { type: 'dateTimeField' } ).of( litteral ) ?? litteral;
      return res[ 0 ].toUpperCase() + res.substring( 1 ).toLowerCase( );
    }
    catch ( err ) {
      console.warn( err );
      return t( litteral );
    }
  };

  const handleTypeChange = ( val: string ) => {
    setType( val as RecurrenceType );
    if ( val == 'weekly' ) {
      setRepeatType( 3 );
    }
    else if ( val == 'daily' || ( val == 'monthly' && repeatType == 0 || repeatType == 3 ) ) {
      setRepeatType( 1 );
    }
  };

  const getValueFromData = () => {
    switch ( type ) {
    case 'daily': return 0;
    case 'weekly': return daysToValue( selectedDays );
    case 'monthly': return Math.ceil( timeStart.date() / 7 );
    default: return 0;
    }
  };

  const getRepeatTypeFromData = () => {
    let _repeatType = repeatType;
    if ( type == 'weekly' ) {  _repeatType = 3; }
    else if ( type == 'daily' ) { _repeatType = 1; }
    return _repeatType;
  };

  const getPriorityFromData = () => {
    if ( haveRecurrence ) {
      switch ( type ) {
      case 'daily': return 1;
      case 'weekly': return 2;
      case 'monthly': return 3;
      default: return 1;
      }
    }
    return 10; // pas de recurrence, priorité maximal
  };

  const handleTimeGridRecurrenceUpdate = async () => {
    if (  timeGridRecurrence.id != null ) {

      await TimeGridRecurrenceService.putTimeGridRecurrence(
        timeGridRecurrence.id,
        {
          id: timeGridRecurrence.id,
          idUser: timeGridRecurrence.idUser,
          idTimeGrid: selectedTimeGrid,
          timeStart: timeStart.format( 'YYYY-MM-DD' ),
          timeEnd: timeEnd ? timeEnd.format( 'YYYY-MM-DD' ) : undefined,
          type: type,
          value: getValueFromData(),
          priority: getPriorityFromData(),
          repeat: repeat,
          repeatType: getRepeatTypeFromData(),
        } as ModifyTimeGridRecurrence, token, dispatch );
    }
    onClose();
  };

  const handleTimeGridRecurrenceCreate = async () => {
    const _timeEnd = timeEnd ? timeEnd.format( 'YYYY-MM-DD' ) : ( haveRecurrence ? undefined : timeStart.format( 'YYYY-MM-DD' ) );
    const _repeatType = getRepeatTypeFromData();
    const _value = getValueFromData();

    if ( usersId && usersId.length >= 1 ) {
      await TimeGridRecurrenceService.postTimeGridRecurrence( {
        idSector: sectorId,
        idTimeGrid: selectedTimeGrid,
        timeStart: timeStart.format( 'YYYY-MM-DD' ),
        timeEnd: _timeEnd,
        type: type,
        value: _value,
        priority: getPriorityFromData(),
        repeat: repeat,
        repeatType: _repeatType,
        usersId: usersId
      } as PostTimeGridRecurrence, token, dispatch );
    }
    else {
      await TimeGridRecurrenceService.postTimeGridRecurrence( {
        idSector: sectorId,
        idTimeGrid: selectedTimeGrid,
        timeStart: timeStart.format( 'YYYY-MM-DD' ),
        timeEnd: _timeEnd,
        type: type,
        value: _value,
        priority: getPriorityFromData(),
        repeat: repeat,
        repeatType: _repeatType,
        usersId: [ timeGridRecurrence.idUser ]
      } as PostTimeGridRecurrence, token, dispatch );
    }
    onClose();
  };

  const handleTimeGridRecurrenceDelete = async () => {
    if ( isDeleteTimeGridRecurrenceModalOpen ) {
      if ( !timeGridRecurrence.id ) {
        onClose();
        return;
      }

      try {
        await TimeGridRecurrenceService.deleteTimeGridRecurrence( timeGridRecurrence.id, token, dispatch );
        onClose();
        toastService.showSuccess( 'translation:timegrid_recurrence_deleted' );
      } catch ( error: unknown ) {
        let errorHandled = false;

        if ( error && typeof error === 'object' && 'response' in error ) {
          const axiosError = error as { response?: { data?: { err?: number } } };
          const errorCode = axiosError.response?.data?.err;

          if ( errorCode === 2037 ) {
            toastService.showError( 'translation:errors.timegrid_recurrence_already_used' );
            errorHandled = true;
          }
        }

        if ( !errorHandled ) {
          const errorMessage = String( error );
          if ( errorMessage.includes( 'ER_ROW_IS_REFERENCED_2' ) ||
               errorMessage.includes( 'foreign key constraint fails' ) ||
               errorMessage.includes( 'FOREIGN KEY constraint failed' ) ||
               errorMessage.includes( 'cannot delete or update a parent row' ) ||
               errorMessage.includes( 'ForeignKeyConstraintError' ) ||
               errorMessage.includes( 'it is saved by existing tasks' ) ||
               errorMessage.includes( 'Task action is already being used' ) ) {
            toastService.showError( 'translation:errors.timegrid_recurrence_already_used' );
          } else {
            toastService.showError( 'translation:errors.delete_error_generic' );
          }
        }
      }
      setIsDeleteTimeGridRecurrenceModalOpen( false );
    }
  };

  // Render
  return (
    <>
      <BaseModal
        isOpen={ timeGridRecurrence != null }
        onClose={ onClose }
        noConfirmationModal={ !haveChanged }
        icon={
          disable ? <TimeGridIcon className="bg-blue-200 p-2 rounded-md" size={12} /> : (
            timeGridRecurrence.id ?
              <EditBoxIcon className="bg-blue-200 p-2 rounded-md" size={10} /> :
              <PlusIcon className="bg-green-200 p-2 rounded-md" size={10} />
          )
        }
        title={ ( timeGridRecurrence.id ? ( disable ? t( 'translation:timegrid_user' ) : t( 'translation:edit_timegrid_user' ) ) : t( 'translation:add_timegrid_user' ) ) }
        footer={
          !disable &&
          <BottomModal
            tableId={ timeGridRecurrence.id ?? undefined }
            formValid={ haveChanged && isValid }
            onCreate={ handleTimeGridRecurrenceCreate }
            onUpdate={ handleTimeGridRecurrenceUpdate }
            onDelete={ () => { setIsDeleteTimeGridRecurrenceModalOpen( true ); } }
            direction='right'
          />
        }
      >
        <div className={ 'py-5 px-7 gap-y-4 bg-white rounded-md shadow-md font-texte flex-col flex'}>
          <div className={'py-5 px-7 gap-y-4 h-fit bg-white overflow-y-visible flex-col rounded-b-xl border font-texte flex'}>
            <SelectInput
              className='w-full'
              labelPosition='left'
              placeholder={ t( 'translation:time_grids' ) }
              label={ t( 'translation:time_grids' ) }
              fetchOptions={ getTimeGridsOptions }
              onSelect={ setSelectedTimegrid }
              selected={ timeGridRecurrence.timeGrid?.id }
              isDisable={disable}
            />

            <div className={`w-full flex ${ ( canHaveRecurrence && haveRecurrence ) ? 'flex-col-reverse' : 'flex-col'}`} >
              <div className={ 'lg:flex gap-x-5' }>
                <div className='w-full'>
                  <div className={ ( !haveRecurrence && 'md:flex-row' ) + ' flex-col flex' }>
                    <label className={( !haveRecurrence && 'md:w-3/12' ) + ' text-gray-600 mr-4 left mt-4'}>
                      { haveRecurrence ? t( 'translation:beginning_of_recurrence' ) : t( 'translation:date_of_time_grid_attribution' ) }
                    </label>
                    <div className='flex gap-x-2 my-2 w-full'>
                      <div className='w-full'>
                        <DatePickerSoloInput
                          defaultValue={ defaultTimeStart }
                          onChange={ val => { setTimeStart( val ?? ( defaultTimeStart ) ); } }
                          dateFormat='L'
                          disable={ disable }
                          minDate={ dayjs().startOf( 'day' ).add( 1, 'day' )}
                          canCancel
                        />
                      </div>
                    </div>
                  </div>
                  { errors.timeStart && <p className="text-red-500 h-6 -mt-2">{ errors.timeStart }</p> }
                </div>

                <div className={ `w-full ${ haveRecurrence ? '' : 'hidden' }` }>
                  <div className={ 'flex-col flex' }>
                    <label className={( !haveRecurrence && 'w-4/12' ) + ' text-gray-600 mr-4 left mt-4 flex'}>
                      { t( 'translation:end_of_recurrence' ) }

                      <>
                        <span
                          id={'roletask-info'}
                          className={'ml-2 cursor-pointer flex items-center justify-center rounded-full bg-gray-200/60 w-6 h-6 text-gray-700 text-sm font-bold'}
                        >
                                    i
                        </span>
                        <Tooltip
                          anchorSelect={'#roletask-info'}
                          className={'z-[1000] max-w-[400px] text-wrap'}
                          place="top"
                          positionStrategy='fixed'
                        >
                          <p className="pt-1">{t( 'translation:recurrence_end_description' )}</p>
                        </Tooltip>
                      </>
                    </label>
                    <div className='flex gap-x-2 my-2 w-full'>
                      <div className='w-full'>
                        <DatePickerSoloInput
                          defaultValue={ defaultTimeEnd }
                          onChange={ setTimeEnd }
                          dateFormat='L'
                          disable={ disable }
                          minDate={ timeStart.startOf( 'day' ) }
                          placeholder={ t( 'translation:no_end' )}
                          canCancel
                          canBeNull
                        />
                      </div>
                    </div>
                  </div>
                  { errors.timeEnd && <p className="text-red-500 h-6 -mt-2">{ errors.timeEnd }</p> }
                </div>
              </div>

              { canHaveRecurrence &&
                  <div className='flex w-1/3 h-fit' id={'fullDay'} >
                    <p className={'text-gray-600 w-full flex items-center left'}> { t( 'translation:recurrence' ) } </p>
                    <div className='w-full flex items-center'>
                      <div className='w-full px-3' >
                        <div className="px-2 pt-2 right w-full justify-end flex items-center">
                          <label className="w-full flex-center items-center cursor-pointer" onClick={ () => { if ( !disable ) { setHaveRecurrence( !haveRecurrence ); } } }>
                            <input
                              type="checkbox"
                              value=""
                              className="sr-only peer"
                              name={'day_switch'}
                              checked={ haveRecurrence }
                              readOnly
                              disabled={disable}
                            />
                            <div className="relative w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all  peer-checked:bg-blue-600"></div>
                          </label>
                        </div>
                      </div>
                    </div>
                    <p className="h-6 -mt-1"/>
                  </div>
              }
            </div>

            { haveRecurrence ? (
              <div className='min-h-44'>
                <div className='flex flex-col gap-3 w-full'>
                  <div className='flex w-full items-center gap-4'>
                    <p className={'text-gray-600 whitespace-nowrap left'}> { t( 'translation:repeat_each' ) } </p>
                    <InputText
                      type='number'
                      min={1}
                      max={10}
                      isRequired
                      value={ repeat }
                      onChange={ ( val: string ) => { setRepeat( Number.parseInt( val ) ?? 0 ); } }
                      className='w-1/3'
                      placeholder={ t( 'translation:repetition' ) }
                      disable={disable}
                    />
                    { disable ?
                      <InputText
                        value={ getLitteral( type ) }
                        disable
                      />
                      :
                      <SelectInput
                        className='w-full'
                        placeholder={ t( 'translation:repetition' ) }
                        fetchOptions={ getRecurrenceType }
                        onSelect={ handleTypeChange }
                        selected={ type }
                        isRequired
                        isDisable={ disable }
                      />
                    }
                  </div>
                </div>


                { ( type == 'monthly' ) &&
                    <>
                      <div className="flex items-center mb-3 mt-4 w-fit p-1 pr-5" onClick={ e => { e.stopPropagation(); if ( !disable ) { setRepeatType( 1 ); } } }>
                        <input
                          id="default-radio-1"
                          type="radio"
                          name="default-radio"
                          value=""
                          checked={ repeatType == 1 }
                          className="w-4 h-4 text-neutral-primary border-default-medium bg-neutral-secondary-medium rounded-full checked:border-brand focus:outline-none"/>
                        <label htmlFor="default-radio-1" className="select-none ms-2 text-gray-700">
                          { type == 'monthly' ?
                            t( 'recurrence_repeat_each_monthly_monthday', { day: timeStart.date() } ) :
                            t( 'recurrence_repeat_each_yearly_monthday', { day: timeStart.date(), month: timeStart.format( 'MMM' ) } )
                          }
                        </label>
                      </div>
                      <div className="flex items-center mb-3 mt-4 w-fit p-1 pr-5" onClick={ e => { e.stopPropagation(); if ( !disable ) { setRepeatType( 2 ); } } }>
                        <input
                          value=""
                          checked={ repeatType == 2 }
                          id="default-radio-2"
                          type="radio"
                          name="default-radio"
                          className="w-4 h-4 text-neutral-primary border-default-medium bg-neutral-secondary-medium rounded-full checked:border-brand focus:outline-none"/>
                        <label htmlFor="default-radio-2" className="select-none ms-2 text-gray-700">
                          {
                            type == 'monthly' ?
                              t( 'recurrence_repeat_each_monthly_weekday', { value: t( ORDINALS[ Math.ceil( timeStart.date() / 7 ) ] ), day: timeStart.format( 'dddd' ) } ) :
                              t( 'recurrence_repeat_each_yearly_weekday', { value: t( ORDINALS[ Math.ceil( timeStart.date() / 7 ) ] ), day: timeStart.format( 'dddd' ), month: timeStart.format( 'MMM' ) } )
                          }
                        </label>
                      </div>
                    </>
                }


                <div className={`mt-4 ${ ( type === 'weekly' ) ? '' : 'hidden'} `}>
                  <WeekDaysSelect
                    label={ t( 'translation:recurrence' ) }
                    onSelectionChange={ setSelectedDays }
                    labelPosition='left'
                    defaultSelectedDays={ selectedDays }
                    shownDays={ isWeekEnd ?
                      { monday: true, tuesday: true, wednesday: true, thursday: true, friday: true, saturday: true, sunday: true } :
                      { monday: true, tuesday: true, wednesday: true, thursday: true, friday: true, saturday: false, sunday: false }
                    }
                    canBeEmpty={false}
                    disable={disable}
                  />
                  { errors.recurrence && <p className="text-red-500 h-6">{ errors.recurrence }</p> }
                </div>
              </div>
            )
              :
              <div className='h-8' />
            }
          </div>
        </div>
      </BaseModal>

      <ConfirmationModal
        isOpen={ isDeleteTimeGridRecurrenceModalOpen }
        onConfirm={ () => {
          setIsDeleteTimeGridRecurrenceModalOpen( false );
          handleTimeGridRecurrenceDelete();
        } }
        onCancel={ () => { setIsDeleteTimeGridRecurrenceModalOpen( false ); } }
        onClose={ () => { setIsDeleteTimeGridRecurrenceModalOpen( false ); } }
        title={ t( 'translation:delete_time_grid_recurrence' ) }
        description={ t( 'translation:are_you_sure_that_you_want_to_delete_this_time_grid_recurrence_it_is_irreversible' ) }
      />
    </>
  );
};

export default TimeGridModal;
